import {ChangeDetectionStrategy, Component, computed, input, output} from '@angular/core';
import type {EChartsOption} from 'echarts';
import {NgxEchartsDirective} from 'ngx-echarts';
import {TradeChainDto} from '../../../../models/generated-models';
import {buildJourneySankey, JOURNEY_COLORS, JOURNEY_CURRENT_COLOR, JOURNEY_WARNING_COLOR, journeySankeyOptions} from './trade-journey-sankey';

interface ChartClick {
  dataType?: string;
  data?: {recordId?: number; current?: boolean};
}

/**
 * The product's journey as a left-to-right Sankey: every lot upstream, this record, every
 * record downstream and the ด่านส่งออก at the end, with ribbon widths in kilograms. Clicking a
 * node opens that record.
 */
@Component({
  selector: 'app-trade-journey-graph',
  standalone: true,
  imports: [NgxEchartsDirective],
  template: `
    @if (hasJourney()) {
      <div class="journey-graph" echarts [options]="options()" [initOpts]="initOpts"
           (chartClick)="onChartClick($event)" aria-label="กราฟการเดินทางของสินค้า"></div>
    } @else {
      <div class="journey-empty" role="status">
        <i class="bi bi-diagram-3" aria-hidden="true"></i>
        <strong>ยังไม่มีเส้นทางให้แสดง</strong>
        <span>กราฟจะแสดงเมื่อรายการนี้ถูกซื้อขายหรือแปรรูปต่อ</span>
      </div>
    }
    <ul class="journey-legend" aria-label="คำอธิบายสี">
      <li><span class="journey-legend-dot" [style.background]="colors.HARVEST"></span>ผลผลิต</li>
      <li><span class="journey-legend-dot" [style.background]="colors.PURCHASE"></span>รับซื้อ</li>
      <li><span class="journey-legend-dot" [style.background]="colors.TRANSFORM"></span>แปรรูป</li>
      <li><span class="journey-legend-dot" [style.background]="colors.CHECKPOINT"></span>ด่านส่งออก</li>
      <li><span class="journey-legend-dot journey-legend-dot--current" [style.border-color]="currentColor"></span>รายการนี้</li>
      @if (showObservations()) {
        <li><span class="journey-legend-dot journey-legend-dot--current" [style.border-color]="warningColor"></span>ข้อสังเกต</li>
      }
    </ul>
  `,
  styles: `
    :host {
      display: block;
      position: absolute;
      inset: 0;
      background: #fff;
    }
    .journey-graph {
      width: 100%;
      height: 100%;
      overflow: hidden;
    }
    .journey-empty {
      position: absolute;
      inset: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 0.35rem;
      padding: 1rem;
      color: #64748b;
      text-align: center;
    }
    .journey-empty i {
      font-size: 2rem;
      color: #cbd5e1;
    }
    .journey-legend {
      position: absolute;
      left: 0.75rem;
      bottom: 0.75rem;
      display: flex;
      flex-wrap: wrap;
      gap: 0.35rem 0.85rem;
      margin: 0;
      padding: 0.35rem 0.75rem;
      list-style: none;
      border: 1px solid #e2e8f0;
      border-radius: 999px;
      background: rgb(255 255 255 / 92%);
      color: #475569;
      font-size: 0.75rem;
      font-weight: 600;
      box-shadow: 0 3px 12px rgb(15 23 42 / 12%);
      pointer-events: none;
    }
    .journey-legend li {
      display: inline-flex;
      align-items: center;
      gap: 0.35rem;
    }
    .journey-legend-dot {
      width: 0.7rem;
      height: 0.7rem;
      border-radius: 2px;
    }
    .journey-legend-dot--current {
      background: #fff;
      border: 3px solid;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TradeJourneyGraphComponent {
  readonly chain = input.required<TradeChainDto | null>();
  /** Officers see the observations (ข้อสังเกต); business users get the plain journey. */
  readonly showObservations = input<boolean>(false);
  /** The record id of a clicked node that is not the one on show. */
  readonly recordSelected = output<number>();

  protected readonly colors = JOURNEY_COLORS;
  protected readonly currentColor = JOURNEY_CURRENT_COLOR;
  protected readonly warningColor = JOURNEY_WARNING_COLOR;
  protected readonly initOpts = {renderer: 'svg' as const};
  protected readonly options = computed<EChartsOption>(() => journeySankeyOptions(this.chain(), this.showObservations()));
  /** A lone record has no ribbons: a Sankey draws nothing for it, so the pane says so instead. */
  protected readonly hasJourney = computed<boolean>(() => buildJourneySankey(this.chain()).links.length > 0);

  protected onChartClick(event: unknown): void {
    const {dataType, data} = (event ?? {}) as ChartClick;
    if (dataType === 'node' && data?.recordId != null && data.current !== true) {
      this.recordSelected.emit(data.recordId);
    }
  }
}
